// Replay a move list against a level. The Worker uses it to rebuild a play's
// state before applying the next word; the game uses it to resume. Every move
// in the list was accepted when it happened, so no dictionary is needed here;
// the geometry is re-checked so a tampered record fails.
import type { PlayLevel } from '../level/schema';
import { chooseAlignment } from './align';
import { removeConstellation } from './remove';
import { tapNode, clearAll } from './select';
import { acceptSelection, spelledWord } from './submit';
import { createPlayState } from './state';
import type { Move, PlayState, WordFacts } from './types';

export class ReplayError extends Error {
  constructor(
    message: string,
    public readonly moveIndex: number,
  ) {
    super(message);
    this.name = 'ReplayError';
  }
}

/** Apply one accepted word: select every node in order, then accept. Throws when a tap is refused. */
export function applyWordMove(state: PlayState, nodes: number[], expectedWord?: string, moveIndex = -1, facts?: WordFacts): PlayState {
  let s = clearAll(state).state;
  for (const id of nodes) {
    const outcome = tapNode(s, id);
    if (outcome.code !== 'selected') {
      throw new ReplayError(`node ${id} refused: ${outcome.code}`, moveIndex);
    }
    s = outcome.state;
  }
  if (s.selection.length !== nodes.length) throw new ReplayError('selection lost nodes', moveIndex);
  const word = spelledWord(s);
  if (expectedWord !== undefined && word !== expectedWord) {
    throw new ReplayError(`nodes spell ${word}, record says ${expectedWord}`, moveIndex);
  }
  if (s.constellations.some((c) => c.word === word)) throw new ReplayError(`${word} already spelled`, moveIndex);
  return acceptSelection(s, facts).state;
}

export function replayMoves(level: PlayLevel, moves: readonly Move[]): PlayState {
  let state = createPlayState(level);
  moves.forEach((move, i) => {
    if (move.type === 'word') {
      state = applyWordMove(state, move.nodes, move.word, i, move.facts);
    } else if (move.type === 'align') {
      const outcome = chooseAlignment(state, move.planet, move.alignment);
      if (outcome.code !== 'aligned') throw new ReplayError(`cannot align planet ${move.planet}: ${outcome.reason}`, i);
      state = outcome.state;
    } else {
      const outcome = removeConstellation(state, move.word);
      if (outcome.code !== 'removed') throw new ReplayError(`cannot remove ${move.word}`, i);
      state = outcome.state;
    }
  });
  return state;
}
